/*
 * Sign in with Google, and an org's sign-in domains (docs/api.md, "Sign in with Google" and "Sign-in domains"). The
 * round trip is a link the browser follows (googleStart), not a fetch: the API sends it to Google and back, and a
 * failure comes back to the page that started it as ?google=<code>, worded here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brandName } from "../lib/brand";
import { api, isApiError } from "./client";
import { keys } from "./keys";
import type { AuthState, Role } from "./types";

/** Whether "Continue with Google" works here: set up, and Hosterr's own address (/api/auth/state says; an older API: no). */
export function useGoogleHere(): boolean {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["auth", "google-here"],
    queryFn: ({ signal }) => api.get<AuthState>("/auth/state", signal).then((s) => s.google === true),
    initialData: () => qc.getQueryData<AuthState>(keys.authState)?.google,
    staleTime: Infinity,
  });
  return q.data === true;
}

/**
 * The link that starts a round trip. `for`: sign_in (the sign-in page), invite (with the invite's token) or link
 * (signed in: link your Google account, or use it for this session). `next`: where to land after.
 */
export function googleStart(p: { for: "sign_in" | "invite" | "link"; next?: string; invite?: string }): string {
  const q = new URLSearchParams({ for: p.for });
  if (p.next && p.next !== "/") q.set("next", p.next);
  if (p.invite) q.set("invite", p.invite);
  return `/api/auth/google/start?${q}`;
}

/** The current page, for `next`, without a ?google= it came back with. */
export function hereForGoogle(): string {
  const u = new URL(window.location.href);
  u.searchParams.delete("google");
  return `${u.pathname}${u.search}`;
}

/** What a ?google= code means, in plain words; null for none, and for "canceled" (they said no at Google: nothing to say). */
export function googleFailure(code: string | null, ctx: { email?: string } = {}): string | null {
  switch (code) {
    case null:
    case "":
    case "canceled":
    case "linked":
      return null;
    case "unavailable":
      return "Google sign-in doesn't work at this address.";
    case "expired":
      return "That took too long, or started in another browser. Try again.";
    case "rate_limited":
      return "Too many attempts. Wait a minute and try again.";
    case "unverified":
      return "Google hasn't verified that email address. Verify it with Google, or sign in another way.";
    case "no_account":
      return `No ${brandName()} account uses that Google account. Sign in another way and link Google in Sign-in and security, or ask your team for an invite.`;
    case "removed":
      return "Google sign-in is off for this account. Sign in another way, then link Google in Sign-in and security.";
    case "other_google":
      return "Your account is linked to another Google account. Use that one, or sign in another way.";
    case "link_first":
      return "Sign in another way first, then link Google under Sign-in and security.";
    case "unmanaged":
      return "Google can make an account only for a Gmail or Google Workspace address. Use a password or a passkey instead.";
    case "taken":
      return "That Google account is linked to someone else's account.";
    case "wrong_account":
      return ctx.email ? `Choose the Google account for ${ctx.email}.` : "Choose the Google account for your own address.";
    case "invite_gone":
      return "This invite has expired or was already used. Ask for a new one.";
    case "invite_mismatch":
      return ctx.email ? `That Google account isn't ${ctx.email}. Choose the one for ${ctx.email}.` : "That Google account isn't the invite's address.";
    case "signed_out":
      return "You were signed out. Sign in and try again.";
    default:
      return "Google sign-in didn't work. Try again.";
  }
}

/* ── Your Google account ────────────────────────────────────────── */

export type GoogleAccount = {
  /** Linking works at this address. */
  available: boolean;
  account: { email: string; linkedAt: string; lastUsedAt: string | null } | null;
  hasPassword: boolean;
  /** Removing it would leave no way in: no password chosen, no passkey. */
  onlyWayIn: boolean;
};

const googleKey = ["me", "google"] as const;

/** Null from an older API (404). */
export function useGoogleAccount() {
  return useQuery({
    queryKey: googleKey,
    queryFn: ({ signal }) =>
      api.get<{ google: GoogleAccount }>("/me/google", signal).then(
        (d) => d.google,
        (e) => {
          if (isApiError(e) && e.status === 404) return null;
          throw e;
        },
      ),
  });
}

export function useRemoveGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ google: GoogleAccount }>("/me/google", {}),
    onSuccess: (r) => qc.setQueryData(googleKey, r.google),
  });
}

/* ── An org's sign-in domains ───────────────────────────────────── */

export type SignInRules = {
  domains: string[];
  /** Its own people at these domains sign in with Google. */
  google: boolean;
  /** Google sign-in is set up on this panel; without it the rule waits. */
  googleSetUp: boolean;
  canChange: boolean;
  /** The org above, whose people follow their own org's rule here. */
  above: { name: string; kind: "platform" | "agency" | "client" } | null;
  /** The agency's own panel address, where Google isn't offered yet. */
  panelDomain: string | null;
  /** Where Google sign-in works: my.hosterr.com. */
  googleAt?: string;
  /** For admins: who's outside the domains (flagged, not removed), and invites still open to other addresses. */
  outside: { members: { userId: string; name: string; email: string; role: Role }[]; invites: { id: string; email: string; role: Role }[] } | null;
};

const rulesKey = (orgId: string) => ["org", orgId, "sign-in-rules"] as const;

/** Null from an older API (404). */
export function useSignInRules(orgId: string) {
  return useQuery({
    queryKey: rulesKey(orgId),
    queryFn: ({ signal }) =>
      api.get<{ rules: SignInRules }>(`/orgs/${orgId}/sign-in-rules`, signal).then(
        (d) => d.rules,
        (e) => {
          if (isApiError(e) && e.status === 404) return null;
          throw e;
        },
      ),
  });
}

export function useSaveSignInRules(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { domains: string[]; google: boolean }) => api.put<{ rules: SignInRules }>(`/orgs/${orgId}/sign-in-rules`, v),
    onSuccess: (r) => qc.setQueryData(rulesKey(orgId), r.rules),
  });
}
